import React, { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";

export default function Modal({ open, onClose, children, title, size = "md", testId, disableOutsideClose = false }) {
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && open && !disableOutsideClose) onClose?.(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose, disableOutsideClose]);

  const sizes = {
    sm: "max-w-md",
    md: "max-w-xl",
    lg: "max-w-3xl",
    xl: "max-w-5xl",
    full: "max-w-7xl",
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          onClick={disableOutsideClose ? undefined : onClose}
          data-testid={testId || "modal"}
        >
          <motion.div
            initial={{ y: 24, opacity: 0, scale: 0.97 }} animate={{ y: 0, opacity: 1, scale: 1 }} exit={{ y: 24, opacity: 0, scale: 0.97 }}
            transition={{ type: "spring", damping: 22, stiffness: 220 }}
            onClick={(e) => e.stopPropagation()}
            className={`relative w-full ${sizes[size]} max-h-[92vh] overflow-y-auto rounded-3xl bg-apex-surface border border-apex-line shadow-2xl`}
          >
            <div className="sticky top-0 z-10 flex items-center justify-between px-6 md:px-8 py-5 border-b border-apex-line bg-apex-surface/90 backdrop-blur">
              <h3 className="font-display text-xl md:text-2xl text-apex-cream font-semibold">{title}</h3>
              <button onClick={onClose} className="w-9 h-9 rounded-full bg-apex-card hover:bg-apex-gold hover:text-apex-bg text-apex-cream flex items-center justify-center transition-colors" data-testid="modal-close-btn">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 md:p-8">
              {children}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
